/**
 * Update URL query parameters by merging provided values into current search string.
 * Adds a parameter if it does not exist, replaces if it does. Optionally replaces history entry.
 * - Null/undefined or empty string values will remove the parameter.
 * - Arrays will be joined with a comma (",").
 *
 * Example:
 * updateRouteQuery({ search: "foo", status: ["1"] });
 */
export const updateRouteQuery = (
  nextParams: Readonly<Record<string, string | number | boolean | ReadonlyArray<string | number | boolean> | null | undefined>>,
  options?: Readonly<{ replace?: boolean }>,
): void => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const keys = Object.keys(nextParams);
  if (keys.length === 0) return;
  for (const key of keys) {
    const value = nextParams[key];
    if (value === undefined || value === null) {
      searchParams.delete(key);
      continue;
    }
    if (Array.isArray(value)) {
      const normalized = value
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v))
        .filter((v) => v !== "");
      if (normalized.length === 0) {
        searchParams.delete(key);
        continue;
      }
      searchParams.set(key, normalized.join(","));
      continue;
    }
    const stringValue = String(value);
    if (stringValue === "") {
      searchParams.delete(key);
      continue;
    }
    searchParams.set(key, stringValue);
  }
  const newSearch = searchParams.toString();
  const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ""}${url.hash}`;
  if (options?.replace) {
    window.history.replaceState(null, "", newUrl);
    return;
  }
  window.history.pushState(null, "", newUrl);
};

export default updateRouteQuery;
